import { InfoDialog } from '@strata/react-dialog';
import { Spinner } from '@strata/react-loading-animation';
import { type ComponentProps } from 'react';

import { useGetSeverityLevelsQuery } from 'store/api/oopsApi';

interface SeverityLevelsInfoModalProps {
    isOpen: boolean;
    onClose: ComponentProps<typeof InfoDialog>['onClose'];
}

const SeverityLevelField = ({ label, value }: { label: string; value: string }) => (
    <div className="padding__bottom--small">
        <strong>{label}:</strong> {value}
    </div>
);

export const SeverityLevelsInfoModal = ({ isOpen, onClose }: SeverityLevelsInfoModalProps) => {
    const {
        data: severityLevels,
        isLoading,
        isError,
    } = useGetSeverityLevelsQuery(undefined, {
        skip: !isOpen,
    });

    const sortedLevels = severityLevels ? [...severityLevels].sort((a, b) => a.id - b.id) : [];

    let dialogContent: React.ReactNode;
    if (isLoading) {
        dialogContent = <Spinner intent={Spinner.Intent.NONE} />;
    } else if (isError) {
        dialogContent = <div>Unable to load severity levels. Please try again later.</div>;
    } else {
        dialogContent = (
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    maxHeight: '60vh',
                    overflowY: 'auto',
                    paddingRight: '8px',
                }}
            >
                {sortedLevels.map((level) => (
                    <div
                        key={level.id}
                        style={{
                            flexShrink: 0,
                            width: '100%',
                            minWidth: 0,
                            padding: '12px',
                            border: '1px solid var(--color-border, #ccc)',
                            borderRadius: '4px',
                            boxSizing: 'border-box',
                        }}
                    >
                        <h4 style={{ margin: '0 0 8px 0' }}>{level.name}</h4>
                        <SeverityLevelField label="Description" value={level.description} />
                        <SeverityLevelField label="User Impact" value={level.user_impact} />
                        <SeverityLevelField label="Business Impact" value={level.business_impact} />
                        <SeverityLevelField label="Infrastructure" value={level.infrastructure} />
                        <SeverityLevelField label="Security" value={level.security} />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <InfoDialog
            dialogTitle="Severity Levels"
            isOpen={isOpen}
            onClose={onClose}
            width={700}
            dialogContent={dialogContent}
        />
    );
};
